import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";

export default function GRNPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    api.get(`/store-entries/${id}`).then((r) => setEntry(r.data));
  }, [id]);

  if (!entry) return <div className="p-8 text-sm text-slate-500">Loading GRN…</div>;

  const totalActual = (entry.items || []).reduce((s, it) => s + Number(it.actual_value || 0), 0);
  const totalGross = (entry.items || []).reduce((s, it) => s + Number(it.gross_value || 0), 0);
  const balance = Number((entry.po_total || 0) - totalGross);

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="no-print max-w-[900px] mx-auto px-4 mb-4 flex justify-between">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={() => window.print()} data-testid="print-grn-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
          <Printer size={14} /> Print GRN
        </button>
      </div>

      <div className="print-page bg-white max-w-[900px] mx-auto p-10 shadow-sm border border-slate-200 text-slate-900" data-testid="grn-page">
        <div className="border-b-2 border-slate-900 pb-3 flex justify-between items-end">
          <div>
            <div className="text-xl font-bold uppercase tracking-wider">Goods Receipt Note</div>
            <div className="text-xs text-slate-600 mt-1 font-mono">{entry.grn_number || "GRN-PENDING"}</div>
          </div>
          <div className="text-right text-xs">
            <div><span className="text-slate-500">Date:</span> {entry.grn_date || (entry.created_at || "").slice(0, 10)}</div>
            <div><span className="text-slate-500">PO No:</span> <span className="font-mono font-semibold">{entry.po_number}</span></div>
          </div>
        </div>

        {/* Supplier + Invoice header */}
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm border border-slate-300 rounded">
          <div className="p-3 border-r border-slate-300">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Supplier</div>
            <div className="font-semibold mt-0.5">{entry.supplier_name || "—"}</div>
            <div className="text-xs font-mono mt-0.5">GST: {entry.supplier_gst || "—"}</div>
          </div>
          <div className="p-3 grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Invoice No.</div>
              <div className="font-mono font-semibold">{entry.invoice_number || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Invoice Date</div>
              <div className="font-mono">{entry.invoice_date || "—"}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Vehicle No.</div>
              <div className="font-mono">{entry.vehicle_number || "—"}</div>
            </div>
          </div>
        </div>

        {/* Items table */}
        <table className="w-full mt-4 text-xs border border-slate-900">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="px-2 py-1.5 border border-slate-900 text-center w-8">S.N.</th>
              <th className="px-2 py-1.5 border border-slate-900 text-left">Item Description</th>
              <th className="px-2 py-1.5 border border-slate-900 text-center">Code</th>
              <th className="px-2 py-1.5 border border-slate-900 text-center">HSN</th>
              <th className="px-2 py-1.5 border border-slate-900 text-center">Unit</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">PO Qty</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Rate</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Actual Qty</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Actual Price</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Actual Val</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Tax %</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Gross Val</th>
            </tr>
          </thead>
          <tbody>
            {(entry.items || []).map((it, idx) => (
              <tr key={idx}>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono">{idx + 1}</td>
                <td className="px-2 py-1.5 border border-slate-300">
                  {it.description}
                  {(it.grade || it.rm_type) && (
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      {it.grade && <>Grade: <span className="font-semibold">{it.grade}</span></>}
                      {it.grade && it.rm_type && " · "}
                      {it.rm_type && <>Type: <span className="font-semibold">{it.rm_type}</span></>}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono">{it.item_code || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono">{it.hsn_code || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-center">{it.unit}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{it.po_quantity}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.po_price || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{it.actual_quantity}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.actual_price || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.actual_value || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.taxation || 0).toFixed(2)}%</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.gross_value || 0).toFixed(2)}</td>
              </tr>
            ))}
            {Array.from({ length: Math.max(0, 3 - (entry.items || []).length) }).map((_, i) => (
              <tr key={`pad-${i}`}>
                {Array.from({ length: 12 }).map((__, j) => (<td key={j} className="px-2 py-1.5 border border-slate-300">&nbsp;</td>))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <div className="border border-slate-300 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">PO Total</div>
            <div className="font-mono font-semibold mt-0.5">₹{Number(entry.po_total || 0).toFixed(2)}</div>
          </div>
          <div className="border border-slate-300 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Gross Value</div>
            <div className="font-mono font-semibold mt-0.5">₹{totalGross.toFixed(2)}</div>
          </div>
          <div className="border border-slate-300 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Balance</div>
            <div className={`font-mono font-semibold mt-0.5 ${balance < 0 ? "text-rose-700" : ""}`}>₹{balance.toFixed(2)}</div>
          </div>
        </div>

        {entry.notes && (
          <div className="mt-4 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Notes</div>
            <div className="mt-0.5">{entry.notes}</div>
          </div>
        )}

        <div className="mt-16 grid grid-cols-3 gap-6 text-xs">
          {["Received By", "Stores In-charge", "Authorised Signatory"].map((l) => (
            <div key={l} className="text-center">
              <div className="border-t border-slate-900 pt-1 mt-12">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
